import type { ReservationWrapper } from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import {
  type PackageItinerarySummary,
  PackageReservationState,
} from "@b2bportal/rbc-bundled-packages-api";

type WrappedReservationsType = {
  [key: string]: ReservationWrapper<PackageItinerarySummary>[];
};

export const PackageItineraryState = {
  Future: "future",
  Present: "present",
  Past: "past",
  Canceled: "canceled",
};

export function transformPackageItineraries(
  itineraries: Record<string, PackageItinerarySummary[]>,
  now = new Date()
) {
  const wrappedReservations = Object.entries(
    itineraries
  ).reduce<WrappedReservationsType>((acc, [key, reservations]) => {
    const wrapped = reservations.map((summary) => ({
      reservation: summary,
      reservationId: summary.customerConfirmationId,
      sortDate: summary.startDate,
      recentFirst:
        key === PackageItineraryState.Past ||
        key === PackageItineraryState.Canceled,
      isCancelled: key === PackageItineraryState.Canceled,
      productType: Product.Package,
    }));
    return { ...acc, [key]: wrapped };
  }, {});

  // Partially cancelled packages should also appear in time-based tabs
  const partiallyCancelledInCurrent = (
    wrappedReservations[PackageItineraryState.Canceled] ?? []
  )
    .filter(
      (w) =>
        w.reservation.state === PackageReservationState.PartiallyCancelled &&
        (!w.reservation.endDate || new Date(w.reservation.endDate) >= now)
    )
    .map((w) => ({ ...w, isCancelled: false, recentFirst: false }));

  const partiallyCancelledInPast = (
    wrappedReservations[PackageItineraryState.Canceled] ?? []
  )
    .filter(
      (w) =>
        w.reservation.state === PackageReservationState.PartiallyCancelled &&
        w.reservation.endDate &&
        new Date(w.reservation.endDate) < now
    )
    .map((w) => ({ ...w, isCancelled: false }));

  return {
    current: [
      ...(wrappedReservations[PackageItineraryState.Future] ?? []),
      ...(wrappedReservations[PackageItineraryState.Present] ?? []),
      ...partiallyCancelledInCurrent,
    ],
    past: [
      ...(wrappedReservations[PackageItineraryState.Past] ?? []),
      ...(wrappedReservations[PackageItineraryState.Canceled] ?? []),
      ...partiallyCancelledInPast,
    ],
  };
}
